import React, { useEffect, useRef, useState } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { ENV_CONFIG } from '../../shared/config/env.config';
import { useLazyGetUploadedFilesQuery } from '../Dashboard/Dashboard.service';
import {
  useIndividualStudListMutation,
  useUpdateStudentsTransferDetailsMutation,
  useOoscSurveyDataMutation,
  useLazyGetVocationalListQuery,
  useLazyGetVocationFormDataQuery,
  useLazyGetStudentDetailQuery,
  type TcStudentRow,
} from './StudentTcDetails.service';
import StudentExitSurvey, { type ExitSurveyPrefill } from './StudentExitSurvey';

interface Props {
  row: TcStudentRow;
  schoolId: number;
  schoolName: string;
  userName: string;
  selectedClass: string;
  onBack: () => void;
  onSaved: () => void;
}

// Sub-stage within the transfer flow: VE students see the exit survey first.
type TransferStage = 'checking' | 'survey' | 'transfer';

// Transfer reasons (Angular `reason` list; "Long Absent" is commented out there).
const REASONS = [
  { value: '2', label: 'Transfer Request by Parent' },
  { value: '3', label: 'Terminal Class' },
  { value: '4', label: 'Dropped Out' },
  { value: '5', label: 'Student expired' },
  { value: '6', label: 'Duplicate EMIS Entry' },
];

const UPLOAD_BUCKET = 'renewalapplicationemis';
const UPLOAD_EXPIRY = 1800;

const StudentTransfer: React.FC<Props> = ({
  row,
  schoolId,
  schoolName,
  userName,
  selectedClass,
  onBack,
  onSaved,
}) => {
  const toast = useRef<Toast>(null);
  const notify = (
    severity: 'success' | 'error' | 'warn' | 'info',
    detail: string
  ): void => toast.current?.show({ severity, detail, life: 4000 });

  const [individualStudList] = useIndividualStudListMutation();
  const [updateTransfer] = useUpdateStudentsTransferDetailsMutation();
  const [ooscSurvey] = useOoscSurveyDataMutation();
  const [getUploadedFiles] = useLazyGetUploadedFilesQuery();
  const [getVocationalList] = useLazyGetVocationalListQuery();
  const [getVocationFormData] = useLazyGetVocationFormDataQuery();
  const [getStudentDetail] = useLazyGetStudentDetailQuery();

  const isRteStudent = row.child_admitted_under_reservation === 'Yes';
  const samEmisId = String(row.NewEmisId ?? '');

  // VE gate: class 11/12 vocational students (mhrd_id === 114) must complete the
  // exit survey before transfer. Non-VE students go straight to transfer.
  const [transferStage, setTransferStage] = useState<TransferStage>('checking');
  const [surveyPrefill, setSurveyPrefill] = useState<ExitSurveyPrefill | null>(
    null
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const uniqueId = String(row.unique_id_no ?? '');
        const [vocForm, detail, vocList] = await Promise.all([
          getVocationFormData({ studId: uniqueId })
            .unwrap()
            .catch(() => null),
          getStudentDetail({
            schoolId,
            stuId: String(row.student_id_in_child_dtl ?? ''),
          })
            .unwrap()
            .catch(() => null),
          getVocationalList({ cls: selectedClass })
            .unwrap()
            .catch(() => null),
        ]);
        if (cancelled) return;

        const allstuds = vocList?.result?.allstuds ?? [];
        const allpaststuds = vocList?.result?.allpaststuds ?? [];
        const matchesVe = (item: Record<string, unknown>): boolean =>
          String(item.unique_id_no ?? '') === uniqueId &&
          Number(item.mhrd_id) === 114;
        const current = allstuds.filter(matchesVe);
        const past = allpaststuds.filter(matchesVe);

        if (current.length > 0 || past.length > 0) {
          const saved = (vocForm?.result?.[0] ?? {}) as Record<string, unknown>;
          const hasSaved = !!vocForm?.dataStatus && !!vocForm.result?.length;
          const jobRole =
            current.length > 0
              ? String(current[0].GroupName ?? '')
              : String(past[0]?.group_name ?? '');
          const fatherName = String(
            (detail?.result?.[0] as Record<string, unknown>)?.father_name ?? ''
          );
          const today = new Date().toISOString().split('T')[0];

          setSurveyPrefill({
            indxId: hasSaved ? ((saved.IndexId as string) ?? '') : '',
            studId: uniqueId,
            name: String(row.name ?? ''),
            fatherName,
            schoolName: hasSaved
              ? String(saved.SchoolName ?? schoolName)
              : schoolName,
            udiseNum: userName,
            groupName: jobRole,
            exitInterviewDate: hasSaved ? String(saved.doe ?? today) : today,
            village: hasSaved ? String(saved.Address ?? '') : '',
            district: hasSaved ? ((saved.District as string) ?? '') : '',
            pincode:
              hasSaved && String(saved.Pincode ?? '').length === 6
                ? String(saved.Pincode)
                : '',
            checked1: hasSaved ? saved.PersMobYN !== '2' : false,
            mobileNo: hasSaved ? String(saved.PersMobile ?? '') : '',
            parentName: hasSaved ? String(saved.GuardianName ?? '') : '',
            parentMobileNo: hasSaved ? String(saved.GuardianMobile ?? '') : '',
            checked2: hasSaved ? saved.HighStudyYN !== '2' : false,
            selectEduTime: hasSaved ? Number(saved.CourseType) || '' : '',
            selectCourseType: hasSaved ? Number(saved.CourseStudy) || '' : '',
            selectStream: hasSaved ? Number(saved.EducaStream) || '' : '',
            typeOfCourse: hasSaved ? String(saved.CourseStudyOthers ?? '') : '',
            typeOfStream: hasSaved ? String(saved.EducaStreamOthers ?? '') : '',
            checked3: hasSaved ? saved.EmpRegisYN !== '2' : false,
            checked4: hasSaved ? saved.NAPSYN !== '2' : false,
            checked5: hasSaved ? saved.JobSelfYN !== '2' : false,
            typeOfEmploye: hasSaved ? Number(saved.InterestedIn) || '' : '',
            PreferredLocation: hasSaved ? Number(saved.Location) || '' : '',
          });
          setTransferStage('survey');
        } else {
          setTransferStage('transfer');
        }
      } catch {
        if (!cancelled) setTransferStage('transfer');
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row]);

  const [reason, setReason] = useState<string>('');
  const [transferDoc, setTransferDoc] = useState<string>('');
  const [originalEmisId, setOriginalEmisId] = useState<string>('');
  const [showFileUpload, setShowFileUpload] = useState(false);

  // reason-6 (duplicate) EMIS validation state
  const [studMatch, setStudMatch] = useState<Record<string, unknown> | null>(
    null
  );
  const [isDuplicate, setIsDuplicate] = useState(false);
  const [emisMessage, setEmisMessage] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const onReasonChange = (value: string): void => {
    setReason(value);
    setOriginalEmisId('');
    setTransferDoc('');
    setStudMatch(null);
    setIsDuplicate(false);
    setEmisMessage('');
    setShowFileUpload(value === '5' || value === '6');
  };

  // Compare the entered EMIS student with the current (duplicate) student on the
  // five identity fields (Angular compareRecords).
  const evaluateDuplicate = (entered: Record<string, unknown>): void => {
    const fields = ['name', 'mother_name', 'father_name', 'dob'];
    const dup = fields.every(
      (f) =>
        String(entered[f] ?? '') ===
        String((row as Record<string, unknown>)[f] ?? '')
    );
    setIsDuplicate(dup);
    setEmisMessage(
      dup
        ? 'This Student ID is a duplicate. You can move it to the Common Pool.'
        : `The entered EMIS ID (${originalEmisId}) is not a duplicate of the current student (${samEmisId}).`
    );
  };

  const checkEmisIdExists = async (emisId: string): Promise<void> => {
    if (emisId === samEmisId) {
      notify('error', 'Both EMIS IDs are the same');
      setOriginalEmisId('');
      setStudMatch(null);
      setIsDuplicate(false);
      setEmisMessage('Cannot use the same EMIS ID as the current student');
      return;
    }
    try {
      const res = await individualStudList({ StudId: emisId }).unwrap();
      if (res.dataStatus && res.result?.length > 0) {
        setStudMatch(res.result[0]);
        setEmisMessage('');
        evaluateDuplicate(res.result[0]);
      } else {
        notify('error', 'Invalid EMIS ID. No student found with this EMIS ID.');
        setStudMatch(null);
        setIsDuplicate(false);
        setOriginalEmisId('');
        setEmisMessage(
          'Invalid EMIS ID. Please enter a valid 10-digit EMIS ID that exists in the system.'
        );
      }
    } catch {
      notify('error', 'Error validating EMIS ID. Please try again.');
      setStudMatch(null);
      setIsDuplicate(false);
    }
  };

  const onEmisChange = (raw: string): void => {
    const value = raw.replace(/[^0-9]/g, '').slice(0, 10);
    setOriginalEmisId(value);
    setStudMatch(null);
    setIsDuplicate(false);
    setEmisMessage('');
    if (value.length === 10) {
      checkEmisIdExists(value);
    } else if (value.length > 0) {
      setEmisMessage('EMIS ID must be exactly 10 digits');
    }
  };

  // ── document upload (signed-URL S3 PUT) ─────────────────────────────────────
  const handleFileUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1200000) {
      notify('warn', 'File size should not exceed 1 MB');
      return;
    }
    const okTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!okTypes.includes(file.type)) {
      notify('warn', 'Please upload only PDF/Photo');
      return;
    }
    const [filename, ext] = file.name.split('.');
    try {
      const params = new URLSearchParams({
        bcktId: UPLOAD_BUCKET,
        ext,
        filename,
        expiry: String(UPLOAD_EXPIRY),
      });
      const signed = await fetch(`${ENV_CONFIG.signedUrlApi}?${params}`).then(
        (r) => r.json()
      );
      if (!signed?.url || !signed?.key) {
        notify('error', 'Error in uploading file, please try again');
        return;
      }
      await fetch(signed.url, { method: 'PUT', body: file });
      setTransferDoc(signed.key);
      notify('success', 'File Uploaded Successfully');
    } catch {
      notify('error', 'Error in uploading file, please try again');
    }
  };

  const handleViewDoc = async (): Promise<void> => {
    if (!transferDoc) return;
    try {
      const res = await getUploadedFiles({
        bucket_name: UPLOAD_BUCKET,
        file_name: transferDoc,
        expiry: UPLOAD_EXPIRY,
      }).unwrap();
      if (res?.url) window.open(res.url, '_blank');
    } catch {
      notify('warn', 'Error opening the file, please try again');
    }
  };

  // ── submit ──────────────────────────────────────────────────────────────────
  const submitTransfer = async (): Promise<void> => {
    if (!reason) {
      notify('warn', 'Please Select The Transfer Reason');
      return;
    }
    if ((reason === '5' || reason === '6') && !transferDoc) {
      notify('warn', 'Please Upload the Document');
      return;
    }

    const value: Record<string, unknown> = {
      name: row.name,
      unique_id_no: row.unique_id_no,
      user_id: row.NewEmisId,
      school_id_transfer: row.school_id,
      class_studying_id: row.class_studying_id,
      process_type: 1,
      admission_no: row.school_admission_no,
      school_doj: row.doj,
      medium_of_ins: row.education_medium_id,
      transfer_reason: reason,
      transfer_doc: transferDoc,
      original_emis_id: originalEmisId,
    };
    if (isRteStudent) value.Rte_status = 'NO';

    setSaving(true);
    try {
      const res = await updateTransfer(value).unwrap();
      if (res) {
        // Fire-and-forget OOSC survey record (mirrors Angular).
        ooscSurvey({
          records: {
            SchoolId: schoolId,
            StudentId: row.student_id_in_child_dtl,
            UserId: row.NewEmisId,
            ClassStudyingId: row.class_studying_id,
            Gender: row.gender,
            TransferDate: new Date(),
            Phase: 2,
            PrvStatus: 1,
            AdmitStatus: 1,
          },
        })
          .unwrap()
          .catch(() => undefined);
        notify('success', res.message ?? 'Student transferred successfully');
        onSaved();
        onBack();
      } else {
        notify('error', 'Something went wrong, unable to update detail');
      }
    } catch {
      notify('error', 'Something went wrong, unable to update detail');
    } finally {
      setSaving(false);
    }
  };

  // RTE students require an untag confirmation first (Angular removeRteTagConfirmation).
  const handleSaveClick = (): void => {
    if (!reason) {
      notify('warn', 'Please select a reason for transfer');
      return;
    }
    if ((reason === '5' || reason === '6') && !transferDoc) {
      notify('warn', 'Please upload the required document');
      return;
    }
    if (isRteStudent) {
      confirmDialog({
        header: 'Confirmation',
        icon: 'pi pi-exclamation-triangle',
        message:
          'This is an RTE student. Proceeding will remove the RTE tag. Do you want to continue?',
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        accept: () => submitTransfer(),
      });
    } else {
      submitTransfer();
    }
  };

  const title = `${row.name ?? ''} - ${row.unique_id_no ?? ''}`;

  // VE gate: show a spinner while checking, then the exit survey for VE
  // students; the transfer form renders once the survey is submitted (or for
  // non-VE students).
  if (transferStage === 'checking') {
    return (
      <div className="card p-4">
        <i className="pi pi-spin pi-spinner me-2" /> Checking student details…
      </div>
    );
  }
  if (transferStage === 'survey' && surveyPrefill) {
    return (
      <StudentExitSurvey
        schoolId={schoolId}
        prefill={surveyPrefill}
        onProceed={() => setTransferStage('transfer')}
        onCancel={onBack}
      />
    );
  }

  return (
    <div className="card card-bg-left p-4 student-transfer">
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="fw-bold card-header tc-card-title p-2 mb-3">{title}</div>

      <div className="row">
        <div className="col-md-9">
          <label className="inMandatory">Reason for The Transfer Student</label>
          <Dropdown
            className="w-100"
            options={REASONS}
            value={reason || null}
            optionLabel="label"
            optionValue="value"
            placeholder="Choose a Reason"
            onChange={(e) => onReasonChange(e.value)}
          />
        </div>
      </div>

      {reason === '6' && (
        <div className="row mt-2">
          <div className="col-md-12">
            <label className="d-block">
              <span style={{ color: '#e71868' }}>Note:</span> To move to the
              common pool for a Duplicate entry, the five fields must be
              identical: Student Name, Date of Birth, Gender, Mother&apos;s
              Name, and Father&apos;s Name. For a mismatch, update the duplicate
              ID&apos;s basic details to match the actual ID.
            </label>
          </div>
        </div>
      )}

      <div className="row mt-3">
        {showFileUpload && (
          <div className="col-md-4">
            <label className="inMandatory">Document for Reason</label>
            <div className="input-group">
              <input
                className="form-control"
                type="file"
                autoComplete="off"
                accept=".png,.jpg,.jpeg,.pdf"
                onChange={handleFileUpload}
              />
              {transferDoc && (
                <button
                  type="button"
                  className="input-group-text text-success"
                  onClick={handleViewDoc}
                >
                  <i className="pi pi-eye" />
                </button>
              )}
            </div>
            <small className="text-danger" style={{ fontSize: 12 }}>
              <span style={{ color: '#f00707' }}>Note: </span>
              (Only PNG, JPEG, JPG, PDF files with a maximum size of 1MB)
            </small>
          </div>
        )}

        {reason === '6' && (
          <div className="col-md-4">
            <label className="inMandatory">Enter the actual EMIS Id</label>
            <InputText
              className="form-control"
              value={originalEmisId}
              maxLength={10}
              placeholder="Enter 10 digit EMIS ID"
              onChange={(e) => onEmisChange(e.target.value)}
            />
            {emisMessage && (
              <small
                className={`d-block ${isDuplicate ? 'text-success' : 'text-danger'}`}
              >
                {emisMessage}
              </small>
            )}
            {studMatch && (
              <small className="text-success d-block">
                Found: {String(studMatch.name ?? '')} (
                {String(studMatch.user_id ?? '')})
              </small>
            )}
          </div>
        )}
      </div>

      {reason === '6' && studMatch && (
        <div className="row mt-3">
          <div className="col-md-3">
            <label>Student Name</label>
            <InputText
              className="form-control"
              value={String(studMatch.name ?? '')}
              readOnly
            />
          </div>
          <div className="col-md-3">
            <label>Mother Name</label>
            <InputText
              className="form-control"
              value={String(studMatch.mother_name ?? '')}
              readOnly
            />
          </div>
          <div className="col-md-3">
            <label>Father Name</label>
            <InputText
              className="form-control"
              value={String(studMatch.father_name ?? '')}
              readOnly
            />
          </div>
          <div className="col-md-3">
            <label>Date of Birth</label>
            <InputText
              className="form-control"
              value={String(studMatch.dob ?? '')}
              readOnly
            />
          </div>
        </div>
      )}

      <div className="text-end mt-4">
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-button-secondary me-2"
          onClick={onBack}
        />
        <Button
          label="Save"
          icon="pi pi-check"
          className="p-button-success"
          loading={saving}
          onClick={handleSaveClick}
        />
      </div>
    </div>
  );
};

export default StudentTransfer;
